<template>
  <div class="pr-root">

    <!-- ===== NAV ===== -->
    <nav class="pr-nav">
      <div class="pr-nav-inner">
        <router-link to="/" class="pr-logo">Mobi<span>FIX</span> PRO</router-link>
        <div class="pr-nav-links">
          <a href="#services" @click.prevent="scrollTo('services')">{{ T.nav.services }}</a>
          <a href="#about" @click.prevent="scrollTo('about')">{{ T.nav.about }}</a>
          <a href="#tracking" @click.prevent="scrollTo('tracking')">{{ T.nav.tracking }}</a>
          <a href="#booking" @click.prevent="scrollTo('booking')">Запис</a>
          <a href="#reviews" @click.prevent="scrollTo('reviews')">{{ T.nav.reviews }}</a>
          <a href="#contacts" @click.prevent="scrollTo('contacts')">{{ T.nav.contacts }}</a>
        </div>
        <div class="pr-nav-auth">
          <template v-if="isAuthenticated">
            <router-link to="/orders" class="pr-nav-link-plain">{{ T.nav.orders }}</router-link>
            <router-link to="/profile" class="pr-nav-link-plain">{{ T.nav.profile }}</router-link>
          </template>
          <template v-else>
            <router-link to="/login" class="pr-btn-outline-sm">{{ T.nav.login }}</router-link>
          </template>
          <router-link to="/orders/create" class="pr-btn-primary-sm">{{ T.nav.orderRepair }}</router-link>
          <button class="pr-lang-toggle" @click="locale.setLang(locale.lang === 'uk' ? 'en' : 'uk')">
            {{ locale.lang === 'uk' ? '🇬🇧 EN' : '🇺🇦 UA' }}
          </button>
        </div>
      </div>
    </nav>

    <!-- ===== HERO BANNER ===== -->
    <section class="pr-hero">
      <div class="pr-hero-bg">
        <div class="pr-blob pr-blob-1"></div>
        <div class="pr-blob pr-blob-2"></div>
        <div class="pr-blob pr-blob-3"></div>
        <div class="pr-grid-dots"></div>
      </div>

      <div class="pr-hero-inner">
        <div class="pr-hero-content">
          <div class="pr-hero-badge">
            <span class="pr-badge-dot"></span>
            {{ T.hero.badge }}
          </div>
          <h1 class="pr-hero-title">
            {{ T.hero.title1 }}<br>
            <span class="pr-hero-gradient">{{ T.hero.title2 }}</span>
          </h1>
          <p class="pr-hero-desc">{{ T.hero.desc }}</p>
          <div class="pr-hero-btns">
            <router-link to="/orders/create" class="pr-btn-hero-primary">
              {{ T.hero.btn1 }}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </router-link>
            <button class="pr-btn-hero-outline" @click="scrollTo('tracking')">{{ T.hero.btn2 }}</button>
          </div>
          <div class="pr-hero-trust">
            <div class="pr-trust-item">⭐ {{ T.hero.trust1 }}</div>
            <div class="pr-trust-sep">·</div>
            <div class="pr-trust-item">✅ {{ T.hero.trust2 }}</div>
            <div class="pr-trust-sep">·</div>
            <div class="pr-trust-item">🔧 {{ T.hero.trust3 }}</div>
          </div>
        </div>

        <div class="pr-hero-visual">
          <div class="pr-phone-mockup">
            <div class="pr-phone-shell">
              <div class="pr-phone-screen">
                <div class="pr-phone-header">
                  <div class="pr-phone-cam"></div>
                  <div class="pr-phone-speaker"></div>
                </div>
                <div class="pr-phone-status">
                  <div class="pr-status-bar">{{ T.hero.phoneStatusBar }}</div>
                  <div class="pr-status-num">PF-2026-4821</div>
                  <div class="pr-phone-steps">
                    <div class="pr-phone-step"><div class="ps-circle">✓</div><div class="ps-text">{{ T.hero.step1 }}</div></div>
                    <div class="pr-phone-step"><div class="ps-circle">✓</div><div class="ps-text">{{ T.hero.step2 }}</div></div>
                    <div class="pr-phone-step"><div class="ps-circle ps-active">⚙</div><div class="ps-text ps-active-text">{{ T.hero.step3 }}</div></div>
                    <div class="pr-phone-step"><div class="ps-circle ps-grey">4</div><div class="ps-text">{{ T.hero.step4 }}</div></div>
                  </div>
                  <div class="pr-phone-progress-wrap"><div class="pr-phone-progress-bar"></div></div>
                  <div class="pr-phone-eta">{{ T.hero.eta }}</div>
                </div>
                <div class="pr-phone-chat-preview">
                  <div class="pr-pcp-msg">🔧 {{ T.hero.techMsg }}</div>
                  <div class="pr-pcp-time">{{ T.hero.now }}</div>
                </div>
              </div>
            </div>
            <div class="pr-phone-glow pr-glow-left"></div>
            <div class="pr-phone-glow pr-glow-right"></div>
          </div>

          <div class="pr-float-card pr-float-1">
            <div class="pr-fc-icon">⚡</div>
            <div><div class="pr-fc-title">{{ T.hero.fast }}</div><div class="pr-fc-sub">{{ T.hero.fastSub }}</div></div>
          </div>
          <div class="pr-float-card pr-float-2">
            <div class="pr-fc-icon pr-fc-green">✓</div>
            <div><div class="pr-fc-title">{{ T.hero.warranty }}</div><div class="pr-fc-sub">{{ T.hero.warrantySub }}</div></div>
          </div>
          <div class="pr-float-card pr-float-3">
            <div class="pr-fc-icon pr-fc-gold">★</div>
            <div><div class="pr-fc-title">{{ T.hero.rating }}</div><div class="pr-fc-sub">{{ T.hero.ratingSub }}</div></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== STATS ===== -->
    <div class="pr-stats">
      <div class="pr-stat"><div class="pr-stat-num">4800+</div><div class="pr-stat-label">{{ T.stats[0] }}</div></div>
      <div class="pr-stat-sep"></div>
      <div class="pr-stat"><div class="pr-stat-num">15 {{ T.stats.minLabel }}</div><div class="pr-stat-label">{{ T.stats[1] }}</div></div>
      <div class="pr-stat-sep"></div>
      <div class="pr-stat"><div class="pr-stat-num">12 {{ T.stats.moLabel }}</div><div class="pr-stat-label">{{ T.stats[2] }}</div></div>
      <div class="pr-stat-sep"></div>
      <div class="pr-stat"><div class="pr-stat-num">4.9 ★</div><div class="pr-stat-label">{{ T.stats[3] }}</div></div>
    </div>

    <!-- ===== КАЛЬКУЛЯТОР ===== -->
    <section class="pr-section" id="calc">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.calc.tag }}</div>
        <div class="pr-section-title">{{ T.calc.title }}</div>
        <div class="pr-section-sub">{{ T.calc.sub }}</div>
      </div>
      <div class="pr-calc-box">
        <div class="pr-calc-selects">
          <div class="pr-calc-sel-wrap">
            <label class="pr-calc-label-sm">{{ T.calc.brandLabel }}</label>
            <select class="pr-calc-select" v-model="calcBrand">
              <option value="">{{ T.calc.brandPlaceholder }}</option>
              <option value="apple">Apple iPhone</option>
              <option value="samsung">Samsung Galaxy</option>
              <option value="xiaomi">Xiaomi</option>
              <option value="other">{{ T.calc.otherBrand }}</option>
            </select>
          </div>
          <div class="pr-calc-sel-wrap">
            <label class="pr-calc-label-sm">{{ T.calc.typeLabel }}</label>
            <select class="pr-calc-select" v-model="calcType">
              <option value="">{{ T.calc.typePlaceholder }}</option>
              <option value="screen">📱 {{ T.calc.types.screen }}</option>
              <option value="battery">🔋 {{ T.calc.types.battery }}</option>
              <option value="camera">📷 {{ T.calc.types.camera }}</option>
              <option value="charge">🔌 {{ T.calc.types.charge }}</option>
              <option value="water">💧 {{ T.calc.types.water }}</option>
              <option value="speaker">🔊 {{ T.calc.types.speaker }}</option>
              <option value="board">🔧 {{ T.calc.types.board }}</option>
              <option value="glass">🪟 {{ T.calc.types.glass }}</option>
              <option value="software">💾 {{ T.calc.types.software }}</option>
              <option value="faceid">🆔 {{ T.calc.types.faceid }}</option>
            </select>
          </div>
        </div>
        <div class="pr-calc-result-wrap" :class="{ active: calcPrice }">
          <div v-if="!calcPrice" class="pr-calc-placeholder">
            <div class="pr-calc-ph-icon">💰</div>
            <div>{{ T.calc.placeholder }}</div>
          </div>
          <div v-else class="pr-calc-result">
            <div class="pr-calc-result-left">
              <div class="pr-calc-result-label">{{ T.calc.resultLabel }}</div>
              <div class="pr-calc-result-price">{{ calcPrice }}</div>
              <div class="pr-calc-result-time" v-if="calcTime">🕐 {{ T.calc.timePrefix }}: {{ calcTime }}</div>
            </div>
            <router-link to="/orders/create" class="pr-btn-calc">{{ T.calc.orderBtn }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== УСЛУГИ ===== -->
    <section class="pr-section" id="services">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.services.tag }}</div>
        <div class="pr-section-title">{{ T.services.title }}</div>
        <div class="pr-section-sub">{{ T.services.sub }}</div>
      </div>
      <div class="pr-services-grid">
        <div class="pr-service-card" v-for="s in services" :key="s.name">
          <div class="pr-sc-icon">{{ s.icon }}</div>
          <div class="pr-sc-name">{{ s.name }}</div>
          <div class="pr-sc-desc">{{ s.desc }}</div>
          <div class="pr-sc-footer">
            <div class="pr-sc-price">{{ s.price }}</div>
            <div class="pr-sc-time">{{ s.time }}</div>
          </div>
          <router-link to="/orders/create" class="pr-sc-btn">{{ T.services.orderBtn }}</router-link>
        </div>
      </div>
    </section>

    <!-- ===== О КОМПАНИИ ===== -->
    <section class="pr-section pr-about-section" id="about">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.about.tag }}</div>
        <div class="pr-section-title">{{ T.about.title }}</div>
        <div class="pr-section-sub">{{ T.about.sub }}</div>
      </div>
      <div class="pr-about-grid">
        <div class="pr-about-text-col">
          <p class="pr-about-p">{{ T.about.p1 }}</p>
          <p class="pr-about-p">{{ T.about.p2 }}</p>
          <p class="pr-about-p">{{ T.about.p3 }}</p>
          <div class="pr-about-certs">
            <div class="pr-about-cert" v-for="c in T.about.certs" :key="c.label">
              <div class="pr-about-cert-icon">{{ c.icon }}</div>
              <div>
                <div class="pr-about-cert-label">{{ c.label }}</div>
                <div class="pr-about-cert-val">{{ c.val }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="pr-about-cards-col">
          <div class="pr-about-card" v-for="f in T.about.facts" :key="f.title">
            <div class="pr-about-card-icon">{{ f.icon }}</div>
            <div class="pr-about-card-title">{{ f.title }}</div>
            <div class="pr-about-card-desc">{{ f.desc }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== КАК МЫ РАБОТАЕМ ===== -->
    <div class="pr-how-wrap">
      <section class="pr-section">
        <div class="pr-section-header">
          <div class="pr-section-tag">{{ T.how.tag }}</div>
          <div class="pr-section-title">{{ T.how.title }}</div>
          <div class="pr-section-sub">{{ T.how.sub }}</div>
        </div>
        <div class="pr-how-steps">
          <div class="pr-how-step" v-for="(step, i) in howSteps" :key="i">
            <div class="pr-how-num">{{ i + 1 }}</div>
            <div class="pr-how-icon">{{ step.icon }}</div>
            <div class="pr-how-title">{{ step.title }}</div>
            <div class="pr-how-desc">{{ step.desc }}</div>
          </div>
        </div>
      </section>
    </div>

    <!-- ===== ТРЕКИНГ ===== -->
    <section class="pr-section" id="tracking">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.tracking.tag }}</div>
        <div class="pr-section-title">{{ T.tracking.title }}</div>
        <div class="pr-section-sub">{{ T.tracking.sub }}</div>
      </div>
      <div class="pr-track-box">
        <div class="pr-track-input-row">
          <input class="pr-track-input" v-model="trackNumber" :placeholder="T.tracking.placeholder" @keyup.enter="trackRepair" />
          <button class="pr-track-btn" @click="trackRepair">{{ T.tracking.btn }}</button>
        </div>
        <transition name="fade">
          <div v-if="trackResult" class="pr-track-result">
            <div class="pr-track-header">
              <div>
                <div class="pr-track-order-num">{{ trackNumber }}</div>
                <div class="pr-track-device">iPhone 14 Pro · {{ T.tracking.sampleService }}</div>
              </div>
              <div class="pr-track-status-badge">{{ T.tracking.statusBadge }}</div>
            </div>
            <div class="pr-steps-row">
              <div class="pr-step-item" v-for="(step, i) in trackSteps" :key="i" :class="{ done: i < 2, active: i === 2 }">
                <div class="pr-step-line" :class="{ active: i < 2 }" v-if="i > 0"></div>
                <div class="pr-step-circle">
                  <span v-if="i < 2">✓</span>
                  <span v-else-if="i === 2">⚙</span>
                  <span v-else>{{ i + 1 }}</span>
                </div>
                <div class="pr-step-label">{{ step }}</div>
              </div>
            </div>
            <div class="pr-track-note">
              <span>{{ T.tracking.eta }}</span>
              <span class="pr-track-notify">{{ T.tracking.notify }}</span>
            </div>
          </div>
        </transition>
      </div>
    </section>

    <!-- ===== ЧАТ ===== -->
    <section class="pr-section" id="chat">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.chat.tag }}</div>
        <div class="pr-section-title">{{ T.chat.title }}</div>
        <div class="pr-section-sub">{{ T.chat.sub }}</div>
      </div>
      <div class="pr-chat-box">
        <div class="pr-chat-header">
          <div class="pr-chat-avatar">АМ</div>
          <div>
            <div class="pr-chat-name">{{ T.chat.masterName }}</div>
            <div class="pr-chat-status"><span class="pr-online-dot"></span> {{ T.chat.online }}</div>
          </div>
          <div class="pr-chat-badges">
            <span class="pr-chat-badge">{{ T.chat.badge1 }}</span>
            <span class="pr-chat-badge">{{ T.chat.badge2 }}</span>
          </div>
        </div>
        <div class="pr-chat-messages" ref="chatMessagesEl">
          <div v-for="(msg, i) in chatMessages" :key="i" class="pr-msg" :class="msg.role">
            <div class="pr-msg-text">{{ msg.text }}</div>
            <div class="pr-msg-time">{{ msg.time }}</div>
          </div>
        </div>
        <div class="pr-chat-input-row">
          <input class="pr-chat-input" v-model="chatInput" :placeholder="T.chat.placeholder" @keyup.enter="sendMsg" :disabled="chatSending" />
          <button class="pr-chat-send" @click="sendMsg" :disabled="chatSending">{{ chatSending ? '...' : '→' }}</button>
        </div>
      </div>
    </section>

    <!-- ===== ОТЗЫВЫ ===== -->
    <section class="pr-section" id="reviews">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.reviews.tag }}</div>
        <div class="pr-section-title">{{ T.reviews.title }}</div>
        <div class="pr-section-sub">{{ T.reviews.sub }}</div>
      </div>
      <div class="pr-reviews-grid">
        <div class="pr-review-card" v-for="r in reviews" :key="r.author">
          <div class="pr-review-top">
            <div class="pr-review-avatar">{{ r.initials }}</div>
            <div>
              <div class="pr-review-author">{{ r.author }}</div>
              <div class="pr-review-device">{{ r.device }}</div>
            </div>
            <div class="pr-review-stars">★★★★★</div>
          </div>
          <div class="pr-review-text">{{ r.text }}</div>
          <div class="pr-review-date">{{ r.date }}</div>
        </div>
      </div>
    </section>

    <!-- ===== ОНЛАЙН-ЗАПИСЬ ===== -->
    <section class="pr-section" id="booking">
      <div class="pr-section-header">
        <div class="pr-section-tag">ЗАПИСАТИСЯ</div>
        <div class="pr-section-title">Онлайн-запис на ремонт</div>
        <div class="pr-section-sub">Обери зручний час — майстер буде чекати саме тебе</div>
      </div>
      <div class="pr-booking-wrap">
        <div v-if="!bookingSent" class="pr-booking-form">

          <!-- Перемикач способу -->
          <div class="pr-delivery-toggle">
            <button class="pr-dtoggle-btn" :class="{ active: bookingForm.delivery === 'personal' }" @click="bookingForm.delivery = 'personal'">
              🏪 Прийду особисто
            </button>
            <button class="pr-dtoggle-btn" :class="{ active: bookingForm.delivery === 'mail' }" @click="bookingForm.delivery = 'mail'">
              📦 Надіслати поштою
            </button>
          </div>

          <div class="pr-booking-grid">
            <div class="pr-booking-field">
              <label class="pr-booking-label">📱 Ваше ім'я</label>
              <input v-model="bookingForm.name" class="pr-booking-input" placeholder="Іван" />
            </div>
            <div class="pr-booking-field">
              <label class="pr-booking-label">📞 Телефон</label>
              <input v-model="bookingForm.phone" type="tel" class="pr-booking-input" placeholder="+38 (0XX) XXX-XX-XX" />
            </div>
            <div class="pr-booking-field">
              <label class="pr-booking-label">🔧 Тип ремонту</label>
              <select v-model="bookingForm.type" class="pr-booking-input">
                <option value="">Оберіть тип...</option>
                <option>Заміна екрана</option>
                <option>Заміна батареї</option>
                <option>Ремонт після води</option>
                <option>Ремонт камери</option>
                <option>Роз'єм зарядки</option>
                <option>Ремонт плати</option>
                <option>Прошивка / ПЗ</option>
                <option>Інша проблема</option>
              </select>
            </div>

            <!-- Особисто: дата і час -->
            <template v-if="bookingForm.delivery === 'personal'">
              <div class="pr-booking-field">
                <label class="pr-booking-label">📅 Дата</label>
                <input v-model="bookingForm.date" type="date" class="pr-booking-input" :min="todayDate" />
              </div>
              <div class="pr-booking-field">
                <label class="pr-booking-label">🕐 Час</label>
                <select v-model="bookingForm.time" class="pr-booking-input">
                  <option value="">Оберіть час...</option>
                  <option>9:00</option><option>10:00</option><option>11:00</option>
                  <option>12:00</option><option>13:00</option><option>14:00</option>
                  <option>15:00</option><option>16:00</option><option>17:00</option><option>18:00</option>
                </select>
              </div>
            </template>

            <!-- Пошта: місто, відділення Нова Пошта -->
            <template v-if="bookingForm.delivery === 'mail'">
              <div class="pr-booking-field">
                <label class="pr-booking-label">🏙 Ваше місто</label>
                <input v-model="bookingForm.city" class="pr-booking-input" placeholder="Київ, Дніпро, Одеса..." />
              </div>
              <div class="pr-booking-field">
                <label class="pr-booking-label">📮 Відділення Нової Пошти</label>
                <input v-model="bookingForm.npBranch" class="pr-booking-input" placeholder="Наприклад: №12" />
              </div>
            </template>

            <div class="pr-booking-field" :style="bookingForm.delivery==='mail' ? 'grid-column:span 2' : ''">
              <label class="pr-booking-label">🎁 Промокод</label>
              <input v-model="bookingForm.promo" class="pr-booking-input" placeholder="Введіть промокод (якщо є)" style="text-transform:uppercase" />
            </div>
          </div>

          <!-- Інфо про пошту -->
          <div v-if="bookingForm.delivery === 'mail'" class="pr-mail-info">
            <div class="pr-mail-step"><span class="pr-mail-num">1</span> Залиш заявку — ми пришлемо точну адресу для відправки</div>
            <div class="pr-mail-step"><span class="pr-mail-num">2</span> Відправ телефон Новою Поштою на наше відділення</div>
            <div class="pr-mail-step"><span class="pr-mail-num">3</span> Ми полагодимо і надішлемо назад за нашим рахунком 🚀</div>
          </div>

          <div class="pr-booking-promo-hint" v-if="promoResult">
            <span v-if="promoResult.valid" class="pr-promo-ok">✅ Промокод застосовано — знижка {{ promoResult.discount }}</span>
            <span v-else class="pr-promo-bad">❌ Невірний промокод</span>
          </div>
          <button class="pr-booking-btn" @click="submitBooking" :disabled="bookingSending">
            {{ bookingSending ? '⏳ Відправляємо...' : (bookingForm.delivery === 'mail' ? '📦 Оформити доставку поштою' : '📅 Записатись на ремонт') }}
          </button>
        </div>
        <div v-else class="pr-booking-success">
          <div class="pr-booking-success-icon">{{ bookingForm.delivery === 'mail' ? '📦' : '🎉' }}</div>
          <h3>{{ bookingForm.delivery === 'mail' ? 'Заявку прийнято!' : 'Запис підтверджено!' }}</h3>
          <p v-if="bookingForm.delivery === 'personal'">Ми зателефонуємо на <strong>{{ bookingForm.phone }}</strong> для підтвердження.<br>Чекаємо вас <strong>{{ bookingForm.date }} о {{ bookingForm.time }}</strong></p>
          <p v-else>Ми зателефонуємо на <strong>{{ bookingForm.phone }}</strong> і нададимо точну адресу для відправки.<br>Після ремонту відправимо назад на <strong>{{ bookingForm.city }}, відділення {{ bookingForm.npBranch }}</strong></p>
          <button class="pr-booking-btn" @click="bookingSent = false; bookingForm.name=''; bookingForm.phone=''">Нова заявка</button>
        </div>
        <div class="pr-booking-aside">
          <div class="pr-booking-info-card">
            <div class="pr-booking-info-icon">✅</div>
            <div class="pr-booking-info-title">Безкоштовна діагностика</div>
            <div class="pr-booking-info-desc">Діагностика завжди безкоштовна, навіть якщо не будете ремонтувати</div>
          </div>
          <div class="pr-booking-info-card">
            <div class="pr-booking-info-icon">⚡</div>
            <div class="pr-booking-info-title">Ремонт при вас</div>
            <div class="pr-booking-info-desc">80% ремонтів виконується за 20–60 хвилин прямо при клієнті</div>
          </div>
          <div class="pr-booking-info-card">
            <div class="pr-booking-info-icon">🔒</div>
            <div class="pr-booking-info-title">Гарантія 12 місяців</div>
            <div class="pr-booking-info-desc">На всі роботи та запчастини. Офіційний гарантійний талон</div>
          </div>
          <div class="pr-booking-promo-block">
            <div class="pr-booking-promo-title">🎁 Наші промокоди:</div>
            <div class="pr-promo-item"><code>FIRST10</code> — 10% знижка на перший ремонт</div>
            <div class="pr-promo-item"><code>MOBIFIX</code> — безкоштовна чистка при ремонті</div>
          </div>
          <div class="pr-booking-info-card pr-mail-aside-card">
            <div>
              <div class="pr-booking-info-title">📦 Ремонт поштою</div>
              <div class="pr-booking-info-desc" style="margin-top:8px">
                Відправляєш телефон Новою Поштою на наше відділення у Харкові. Після ремонту ми надсилаємо назад — <strong>доставка за нашим рахунком!</strong>
                <div style="margin-top:8px;color:#1d9e75;font-weight:600">Доставка включена в ціну ремонту</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== ПРОГРАМА ЛОЯЛЬНОСТІ ===== -->
    <section class="pr-loyalty-section">
      <div class="pr-section pr-loyalty-inner">
        <div class="pr-section-header">
          <div class="pr-section-tag" style="background:rgba(255,255,255,0.15);color:#fff">БОНУСИ</div>
          <div class="pr-section-title" style="color:#fff">Програма лояльності MobiFIX PRO</div>
          <div class="pr-section-sub" style="color:rgba(255,255,255,0.75)">Запрошуй друзів — отримуй гроші на рахунок</div>
        </div>
        <div class="pr-loyalty-grid">
          <div class="pr-loyalty-card">
            <div class="pr-loyalty-icon">👥</div>
            <div class="pr-loyalty-title">Запроси друга</div>
            <div class="pr-loyalty-desc">Поділись своїм реферальним кодом з другом. Коли він зареєструється — ти отримаєш 50 грн на бонусний рахунок</div>
          </div>
          <div class="pr-loyalty-card">
            <div class="pr-loyalty-icon">🔧</div>
            <div class="pr-loyalty-title">Друг відремонтував</div>
            <div class="pr-loyalty-desc">Коли запрошений друг оплачує ремонт — ти отримуєш <strong>10% від суми ремонту</strong> як кешбек на рахунок</div>
          </div>
          <div class="pr-loyalty-card">
            <div class="pr-loyalty-icon">💰</div>
            <div class="pr-loyalty-title">Витрачай бонуси</div>
            <div class="pr-loyalty-desc">Накопиченими бонусами можна оплатити до 50% вартості свого наступного ремонту</div>
          </div>
        </div>
        <div class="pr-loyalty-cta">
          <router-link to="/register" class="pr-loyalty-btn">🎁 Зареєструватись і отримати код</router-link>
          <div class="pr-loyalty-note">Вже є акаунт? <router-link to="/profile" class="pr-loyalty-link">Знайди свій реферальний код у профілі</router-link></div>
        </div>
      </div>
    </section>

    <!-- ===== КАК НАС НАЙТИ (КАРТА) ===== -->
    <section class="pr-section" id="contacts">
      <div class="pr-section-header">
        <div class="pr-section-tag">{{ T.contacts.tag }}</div>
        <div class="pr-section-title">{{ T.contacts.title }}</div>
        <div class="pr-section-sub">{{ T.contacts.sub }}</div>
      </div>
      <div class="pr-contacts-wrap">
        <div class="pr-contacts-info">
          <a href="https://maps.google.com/?q=49.9974233,36.3409755" target="_blank" rel="noopener" class="pr-contact-card pr-contact-card-link">
            <div class="pr-contact-icon">📍</div>
            <div>
              <div class="pr-contact-label">{{ T.contacts.addressLabel }}</div>
              <div class="pr-contact-val">{{ T.contacts.addressVal }}</div>
            </div>
          </a>
          <a href="tel:+380682772822" class="pr-contact-card pr-contact-card-link">
            <div class="pr-contact-icon">📞</div>
            <div>
              <div class="pr-contact-label">{{ T.contacts.phoneLabel }}</div>
              <div class="pr-contact-val pr-contact-purple">+38 (068) 277-28-22</div>
            </div>
          </a>
          <div class="pr-contact-card">
            <div class="pr-contact-icon">🕐</div>
            <div>
              <div class="pr-contact-label">{{ T.contacts.hoursLabel }}</div>
              <div class="pr-contact-val">{{ T.contacts.hoursVal }}</div>
            </div>
          </div>
          <a href="mailto:info@mobifixpro.net" class="pr-contact-card pr-contact-card-link">
            <div class="pr-contact-icon">✉️</div>
            <div>
              <div class="pr-contact-label">{{ T.contacts.emailLabel }}</div>
              <div class="pr-contact-val pr-contact-purple">info@mobifixpro.net</div>
            </div>
          </a>
          <router-link to="/orders/create" class="pr-contact-cta-btn">{{ T.contacts.ctaBtn }}</router-link>
        </div>
        <div class="pr-map-wrap">
          <iframe
            src="https://maps.google.com/maps?q=49.9974233,36.3409755&z=17&output=embed"
            class="pr-map-iframe"
            title="MobiFIX PRO — розташування на карті Харкова"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>
    </section>

    <!-- ===== CTA ===== -->
    <section class="pr-cta-section">
      <div class="pr-cta-inner">
        <div class="pr-cta-icon">{{ T.cta.icon }}</div>
        <h2>{{ T.cta.title }}</h2>
        <p>{{ T.cta.desc }}</p>
        <div class="pr-cta-btns">
          <router-link to="/orders/create" class="pr-btn-hero-primary">{{ T.cta.btn1 }}</router-link>
          <a href="tel:+380682772822" class="pr-btn-hero-outline-light">{{ T.cta.btn2 }}</a>
        </div>
      </div>
    </section>

    <!-- ===== ФУТЕР ===== -->
    <footer class="pr-footer">
      <div class="pr-footer-inner">
        <div class="pr-footer-brand">
          <div class="pr-footer-logo">Mobi<span>FIX</span> PRO</div>
          <p>{{ T.footer.brand }}</p>
        </div>
        <div class="pr-footer-col">
          <div class="pr-footer-title">{{ T.footer.services }}</div>
          <a class="pr-footer-link" @click.prevent="scrollTo('services')">{{ T.footer.screenReplacement }}</a>
          <a class="pr-footer-link" @click.prevent="scrollTo('services')">{{ T.footer.batteryReplacement }}</a>
          <a class="pr-footer-link" @click.prevent="scrollTo('services')">{{ T.footer.waterRepair }}</a>
          <a class="pr-footer-link" @click.prevent="scrollTo('services')">{{ T.footer.cameraRepair }}</a>
        </div>
        <div class="pr-footer-col">
          <div class="pr-footer-title">{{ T.footer.contacts }}</div>
          <a href="tel:+380682772822" class="pr-footer-link">📞 +38 (068) 277-28-22</a>
          <a href="mailto:info@mobifixpro.net" class="pr-footer-link">✉️ info@mobifixpro.net</a>
          <a class="pr-footer-link" @click.prevent="scrollTo('contacts')">📍 {{ T.contacts.addressVal }}</a>
          <div class="pr-footer-link">🕐 {{ T.contacts.hoursVal }}</div>
        </div>
        <div class="pr-footer-col">
          <div class="pr-footer-title">{{ T.footer.nav }}</div>
          <router-link to="/orders/create" class="pr-footer-link">{{ T.footer.orderRepair }}</router-link>
          <a class="pr-footer-link" @click.prevent="scrollTo('tracking')">{{ T.footer.trackRepair }}</a>
          <router-link to="/login" class="pr-footer-link">{{ T.footer.login }}</router-link>
          <router-link to="/register" class="pr-footer-link">{{ T.footer.register }}</router-link>
        </div>
      </div>
      <div class="pr-footer-bottom">
        <span>{{ T.footer.copyright }}</span>
        <span>{{ T.footer.tagline }}</span>
      </div>
    </footer>

  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useLocaleStore } from '../stores/locale';
import { useSocket } from '../composables/useSocket';
import axios from 'axios';

const CHAT_API = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api') + '/chat';

function generateSessionId() {
  try { const s = sessionStorage.getItem('chat_session'); if (s) return s; } catch {}
  const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
  try { sessionStorage.setItem('chat_session', id); } catch {}
  return id;
}

const TX = {
  uk: {
    nav: { services: 'Послуги', about: 'Про нас', tracking: 'Трекінг', reviews: 'Відгуки', contacts: 'Контакти', orders: 'Мої замовлення', profile: 'Профіль', login: 'Увійти', orderRepair: 'Замовити ремонт' },
    hero: {
      badge: 'ПРАЦЮЄМО ЗАРАЗ · 9:00–19:00',
      title1: 'Ремонт телефонів', title2: 'нового покоління',
      desc: 'Діагностика за 15 хвилин. Ремонт при вас. Гарантія 12 місяців.\nСтеж за статусом онлайн — у реальному часі.',
      btn1: 'Замовити ремонт', btn2: 'Відстежити ремонт',
      trust1: 'Рейтинг 4.9/5', trust2: 'Гарантія 12 міс', trust3: '4800+ ремонтів',
      phoneStatusBar: 'Статус замовлення', step1: 'Прийнятий', step2: 'Діагноз', step3: 'Ремонт', step4: 'Готовий',
      eta: 'Готовий через ~2 години', techMsg: 'Майстер: міняємо екран...', now: 'зараз',
      fast: 'Швидко', fastSub: 'від 20 хвилин', warranty: 'Гарантія', warrantySub: '12 місяців', rating: '4.9 рейтинг', ratingSub: '567 відгуків'
    },
    stats: { 0: 'ремонтів виконано', 1: 'безкоштовна діагностика', 2: 'гарантія на ремонт', 3: 'середня оцінка', minLabel: 'хв', moLabel: 'міс' },
    calc: {
      tag: 'КАЛЬКУЛЯТОР', title: 'Дізнайся ціну ремонту', sub: 'Вибери бренд і тип ремонту — отримай вартість за 5 секунд',
      brandLabel: 'Бренд телефону', brandPlaceholder: 'Вибери бренд', otherBrand: 'Інший бренд',
      typeLabel: 'Тип ремонту', typePlaceholder: 'Тип ремонту',
      types: { screen: 'Заміна екрана', battery: 'Заміна батареї', camera: 'Ремонт камери', charge: 'Роз\'єм зарядки', water: 'Потрапила вода', speaker: 'Динамік/мікрофон', board: 'Ремонт плати', glass: 'Тільки скло', software: 'Прошивка/ПЗ', faceid: 'Face ID / Touch ID' },
      placeholder: 'Вибери параметри вище, щоб дізнатися вартість',
      resultLabel: 'Орієнтовна вартість', timePrefix: 'Час', orderBtn: 'Замовити →'
    },
    services: { tag: 'ПОСЛУГИ', title: 'Що ми ремонтуємо', sub: 'Швидко, якісно, з гарантією на кожну роботу', orderBtn: 'Замовити' },
    about: {
      tag: 'ПРО НАС', title: 'MobiFIX PRO — ваш сервіс у Харкові', sub: 'Більше 8 років ремонтуємо телефони. Чесно, швидко і з гарантією.',
      p1: 'MobiFIX PRO — сервісний центр у Харкові, що спеціалізується на ремонті смартфонів та планшетів усіх брендів. Ми відкрились у 2018 році і за цей час виконали понад 4800 ремонтів. Наша команда — сертифіковані майстри з багаторічним досвідом роботи з Apple, Samsung, Xiaomi, Huawei та іншими брендами.',
      p2: 'Ми використовуємо лише оригінальні та сертифіковані запчастини від перевірених постачальників. Кожен ремонт супроводжується безкоштовною діагностикою та гарантією 12 місяців. Більшість ремонтів виконується прямо при клієнті — без зайвого очікування.',
      p3: 'Ми не беремо гроші за діагностику і ніколи не приховуємо кінцеву вартість. Ви дізнаєтесь точну ціну до початку ремонту і самі вирішуєте — погоджуватися чи ні. Саме тому 92% наших клієнтів повертаються до нас знову.',
      certs: [
        { icon: '🏅', label: 'Apple Authorized', val: 'Сертифіковані майстри' },
        { icon: '⭐', label: 'Samsung Partner', val: 'Офіційні запчастини' },
        { icon: '🔒', label: 'Гарантія', val: '12 місяців на всі роботи' },
      ],
      facts: [
        { icon: '📅', title: '8 років на ринку', desc: 'Відкрились у 2018 році. Пережили все — і залишились найкращими.' },
        { icon: '👨‍🔧', title: '6 сертифікованих майстрів', desc: 'Кожен майстер — вузький спеціаліст зі своєю зоною відповідальності.' },
        { icon: '🔩', title: 'Оригінальні запчастини', desc: 'Тільки перевірені постачальники. Підроблені деталі — ніколи.' },
        { icon: '⚡', title: 'Ремонт при вас', desc: '80% ремонтів — від 20 хвилин до 1 години. Чекайте в зручному кріслі.' },
        { icon: '📊', title: '98% успішних ремонтів', desc: 'Навіть складні випадки — розбита плата, вода — ми беремось і робимо.' },
        { icon: '💬', title: 'Завжди на зв\'язку', desc: 'Статус ремонту онлайн. Майстер у Telegram. Відповідаємо швидко.' },
      ]
    },
    how: {
      tag: 'ПРОЦЕС', title: 'Як ми працюємо', sub: 'Прозоро і швидко — від заявки до готового телефону',
      steps: [
        { icon: '📝', title: 'Залиш заявку', desc: 'Заповни форму онлайн або зателефонуй. Займе 2 хвилини' },
        { icon: '🔍', title: 'Діагностика', desc: 'Майстер безкоштовно перевірить пристрій за 15 хвилин' },
        { icon: '⚙️', title: 'Ремонт', desc: 'Ремонтуємо при тобі або можеш залишити і забрати пізніше' },
        { icon: '✅', title: 'Готово!', desc: 'Отримай телефон з гарантією 12 місяців на всі роботи' }
      ]
    },
    tracking: {
      tag: 'ТРЕКІНГ', title: 'Відстежити ремонт', sub: 'Введи номер заявки і дивися у реальному часі, де твій телефон',
      placeholder: 'Номер заявки, наприклад: PF-2026-1337', btn: 'Знайти',
      sampleService: 'Заміна екрана', statusBadge: 'В процесі ремонту',
      eta: '🕐 Очікуваний час готовності: сьогодні, ~18:30', notify: 'Повідомимо по SMS'
    },
    chat: {
      tag: 'ЧАТ', title: 'Чат з майстром', sub: 'Постав будь-яке питання — відповімо швидко і чесно',
      masterName: 'Максим — старший майстер', online: 'онлайн зараз',
      badge1: '📱 Apple Expert', badge2: '⭐ 12 років досвіду', placeholder: 'Напиши питання про ремонт...',
      greeting: 'Привіт! Я Максим, старший майстер. Розкажи про проблему з телефоном — скажу точну ціну і терміни 👋',
      replies: [
        'Зрозумів! Це часта проблема. Зазвичай займає 30–60 хвилин, вартість від 800 грн. Можемо зробити сьогодні 🔧',
        'Приходь прямо зараз — майстер вільний! Діагностика безкоштовно.',
        'Використовуємо лише оригінальні та сертифіковані запчастини. Гарантія 12 місяців.',
        'Добре! Залиш номер телефону і ми передзвонимо, або оформлюй заявку онлайн 👆'
      ]
    },
    reviews: { tag: 'ВІДГУКИ', title: 'Клієнти про нас', sub: 'Понад 4800 задоволених клієнтів' },
    contacts: {
      tag: 'КОНТАКТИ', title: 'Як нас знайти', sub: 'Ми поруч — зручно дістатися з будь-якої точки Харкова',
      addressLabel: 'Адреса', addressVal: 'Харків, просп. Ювілейний, 57а',
      phoneLabel: 'Телефон', hoursLabel: 'Режим роботи', hoursVal: 'Пн–Сб 9:00–19:00 · Нд — вихідний',
      emailLabel: 'Email', ctaBtn: '→ Оформити замовлення'
    },
    cta: { icon: '📱', title: 'Зламався телефон?', desc: 'Приноси прямо зараз — діагностика безкоштовно. Або оформи онлайн-заявку і ми передзвонимо.', btn1: 'Оформити заявку', btn2: '📞 Зателефонувати' },
    footer: { brand: 'Професійний ремонт телефонів\nта планшетів у Харкові', services: 'Послуги', contacts: 'Контакти', nav: 'Навігація', screenReplacement: 'Заміна екрана', batteryReplacement: 'Заміна батареї', waterRepair: 'Ремонт після води', cameraRepair: 'Ремонт камери', orderRepair: 'Оформити замовлення', trackRepair: 'Відстежити ремонт', login: 'Увійти', register: 'Реєстрація', copyright: '© 2026 MobiFIX PRO — Харків. Всі права захищені.', tagline: 'Гарантія 12 місяців · Діагностика безкоштовно · 4800+ ремонтів' },
    services_data: [
      { icon: '📱', name: 'Заміна екрана', desc: 'Тріщини, розбитий дисплей, не реагує сенсор. Оригінальні матриці.', price: 'від 800 грн', time: '30–60 хв' },
      { icon: '🔋', name: 'Заміна батареї', desc: 'Телефон мало тримає, не заряджається, вздулася батарея', price: 'від 400 грн', time: '20–40 хв' },
      { icon: '📷', name: 'Ремонт камери', desc: 'Мутні фото, не фокусується, чорний екран камери', price: 'від 600 до 4500 грн', time: '40–90 хв' },
      { icon: '💧', name: 'Після води', desc: 'Впав у воду, не вмикається, мерехтить екран', price: 'від 500 грн', time: '60–120 хв' },
      { icon: '🔌', name: "Роз'єм зарядки", desc: 'Не заряджається, поганий контакт, USB не визначається', price: 'від 350 до 3000 грн', time: '20–30 хв' },
      { icon: '🔊', name: 'Динамік/мікрофон', desc: 'Тихий звук, хрипить, співрозмовник не чує', price: 'від 350 грн', time: '30–50 хв' },
      { icon: '🔧', name: 'Ремонт плати', desc: 'Не вмикається, зависає, не бачить SIM-картку — ремонт мікросхем', price: 'від 2000 грн', time: '1–3 дні' },
      { icon: '💾', name: 'Відновлення даних', desc: 'Відновлення фото, контактів, повідомлень після скидання або поломки', price: 'від 500 грн', time: '1–4 год' },
      { icon: '🪟', name: 'Заміна скла (без матриці)', desc: 'Тільки скло пошкоджено — замінюємо без заміни дисплея', price: 'від 1100 грн', time: '60–90 хв' },
      { icon: '📲', name: 'Прошивка / відновлення ПЗ', desc: 'Завис на логотипі, не оновлюється, вірус — перепрошиваємо', price: 'від 500 до 3000 грн', time: '30–60 хв' },
      { icon: '🆔', name: 'Ремонт Face ID / Touch ID', desc: 'Не розпізнає обличчя або відбиток — замінюємо датчик', price: 'від 900 грн', time: '60–90 хв' },
      { icon: '📶', name: 'Ремонт Wi-Fi / Bluetooth', desc: 'Не підключається до мережі, слабкий сигнал — ремонт антени', price: 'від 700 грн', time: '60–120 хв' },
    ],
    trackSteps: ['Прийнятий', 'Діагностика', 'Ремонт', 'Перевірка', 'Готовий'],
    reviews_data: [
      { initials: 'ДК', author: 'Дмитро К.', device: 'iPhone 14 Pro · Екран', text: 'Полагодили iPhone за 40 хвилин прямо при мені. Якість відмінна, гарантію дали на рік. Дуже задоволений!', date: '2 дні тому' },
      { initials: 'АМ', author: 'Анна М.', device: 'Samsung S23 · Батарея', text: 'Розряджався за 3 години, замінили батарею швидко і недорого. Трекінг статусу дуже зручний!', date: '5 днів тому' },
      { initials: 'СП', author: 'Сергій П.', device: 'Xiaomi · Після води', text: 'Телефон впав у воду. Думав все — але майстри врятували за 2 години. Працює як новий!', date: '1 тиждень тому' },
      { initials: 'КВ', author: 'Катя В.', device: 'iPhone 13 · Камера', text: 'Перестала працювати основна камера. Полагодили за 50 хвилин, ціна адекватна. Рекомендую!', date: '2 тижні тому' },
    ]
  },
  en: {
    nav: { services: 'Services', about: 'About Us', tracking: 'Track', reviews: 'Reviews', contacts: 'Contacts', orders: 'My Orders', profile: 'Profile', login: 'Log In', orderRepair: 'Order Repair' },
    hero: {
      badge: 'OPEN NOW · 9:00–19:00',
      title1: 'Phone Repair', title2: 'of a new generation',
      desc: 'Diagnosis in 15 minutes. Repair while you wait. 12-month warranty.\nTrack your repair status online — in real time.',
      btn1: 'Order Repair', btn2: 'Track Repair',
      trust1: 'Rating 4.9/5', trust2: '12 mo Warranty', trust3: '4800+ Repairs',
      phoneStatusBar: 'Order status', step1: 'Accepted', step2: 'Diagnosis', step3: 'Repair', step4: 'Ready',
      eta: 'Ready in ~2 hours', techMsg: 'Tech: replacing screen...', now: 'now',
      fast: 'Fast', fastSub: 'from 20 minutes', warranty: 'Warranty', warrantySub: '12 months', rating: '4.9 rating', ratingSub: '567 reviews'
    },
    stats: { 0: 'repairs completed', 1: 'free diagnosis', 2: 'repair warranty', 3: 'average rating', minLabel: 'min', moLabel: 'mo' },
    calc: {
      tag: 'CALCULATOR', title: 'Find out repair cost', sub: 'Choose brand and repair type — get the price in 5 seconds',
      brandLabel: 'Phone brand', brandPlaceholder: 'Choose brand', otherBrand: 'Other brand',
      typeLabel: 'Repair type', typePlaceholder: 'Repair type',
      types: { screen: 'Screen replacement', battery: 'Battery replacement', camera: 'Camera repair', charge: 'Charging port', water: 'Water damage', speaker: 'Speaker/microphone', board: 'Motherboard repair', glass: 'Glass only', software: 'Software/flashing', faceid: 'Face ID / Touch ID' },
      placeholder: 'Choose parameters above to see the price',
      resultLabel: 'Estimated cost', timePrefix: 'Time', orderBtn: 'Order →'
    },
    services: { tag: 'SERVICES', title: 'What we repair', sub: 'Fast, quality service with warranty on every job', orderBtn: 'Order' },
    about: {
      tag: 'ABOUT US', title: 'MobiFIX PRO — your repair shop in Kharkiv', sub: 'Over 8 years of repairing phones. Honest, fast, and with a warranty.',
      p1: 'MobiFIX PRO is a service center in Kharkiv specializing in smartphone and tablet repair for all brands. We opened in 2018 and have completed over 4,800 repairs since then. Our team consists of certified technicians with years of experience working with Apple, Samsung, Xiaomi, Huawei, and other brands.',
      p2: 'We use only original and certified spare parts from trusted suppliers. Every repair comes with a free diagnosis and a 12-month warranty. Most repairs are done right in front of you — no unnecessary waiting.',
      p3: "We don't charge for diagnostics and never hide the final cost. You'll know the exact price before the repair begins, and you decide whether to proceed. That's why 92% of our clients come back to us again.",
      certs: [
        { icon: '🏅', label: 'Apple Authorized', val: 'Certified technicians' },
        { icon: '⭐', label: 'Samsung Partner', val: 'Official spare parts' },
        { icon: '🔒', label: 'Warranty', val: '12 months on all work' },
      ],
      facts: [
        { icon: '📅', title: '8 years in business', desc: 'Opened in 2018. Survived everything — and stayed the best.' },
        { icon: '👨‍🔧', title: '6 certified technicians', desc: 'Every tech is a narrow specialist with their own area of responsibility.' },
        { icon: '🔩', title: 'Original spare parts', desc: 'Trusted suppliers only. Counterfeit parts — never.' },
        { icon: '⚡', title: 'Repair while you wait', desc: '80% of repairs take 20 minutes to 1 hour. Wait in a comfortable chair.' },
        { icon: '📊', title: '98% successful repairs', desc: 'Even complex cases — cracked boards, water damage — we take them on and deliver.' },
        { icon: '💬', title: 'Always in touch', desc: 'Repair status online. Master in Telegram. We reply fast.' },
      ]
    },
    how: {
      tag: 'PROCESS', title: 'How we work', sub: 'Transparent and fast — from request to ready phone',
      steps: [
        { icon: '📝', title: 'Submit a request', desc: 'Fill in the form online or call us. Takes 2 minutes' },
        { icon: '🔍', title: 'Diagnosis', desc: 'Technician checks the device for free in 15 minutes' },
        { icon: '⚙️', title: 'Repair', desc: 'We repair while you watch, or leave it and pick up later' },
        { icon: '✅', title: 'Done!', desc: 'Get your phone with a 12-month warranty on all work' }
      ]
    },
    tracking: {
      tag: 'TRACKING', title: 'Track Repair', sub: 'Enter your order number and see in real time where your phone is',
      placeholder: 'Order number, e.g.: PF-2026-1337', btn: 'Find',
      sampleService: 'Screen Replacement', statusBadge: 'In Repair Process',
      eta: '🕐 Expected ready time: today, ~18:30', notify: "We'll SMS you"
    },
    chat: {
      tag: 'CHAT', title: 'Chat with Technician', sub: 'Ask any question — we answer fast and honestly',
      masterName: 'Maxim — Senior Technician', online: 'online now',
      badge1: '📱 Apple Expert', badge2: '⭐ 12 years exp', placeholder: 'Write your repair question...',
      greeting: "Hi! I'm Alex, senior technician. Tell me about your phone issue — I'll give you an exact price and timeline 👋",
      replies: [
        'Got it! This is a common issue. Usually takes 30–60 minutes, price from ₴800. We can do it today 🔧',
        'Come in right now — the technician is available! Diagnosis is free.',
        'We use only original and certified parts. 12-month warranty.',
        'Great! Leave your phone number and we\'ll call you back, or submit a request online 👆'
      ]
    },
    reviews: { tag: 'REVIEWS', title: 'Our customers', sub: 'Over 4800 satisfied customers' },
    contacts: {
      tag: 'CONTACTS', title: 'How to find us', sub: 'Conveniently located — easy to reach from anywhere in Kharkiv',
      addressLabel: 'Address', addressVal: 'Kharkiv, Yuvileinyi Ave, 57a',
      phoneLabel: 'Phone', hoursLabel: 'Working hours', hoursVal: 'Mon–Sat 9:00–19:00 · Sun — closed',
      emailLabel: 'Email', ctaBtn: '→ Order Repair'
    },
    cta: { icon: '📱', title: 'Broken phone?', desc: "Come right now — diagnosis is free. Or submit an online request and we'll call you back.", btn1: 'Submit request', btn2: '📞 Call us' },
    footer: { brand: 'Professional phone and tablet\nrepair in Kharkiv', services: 'Services', contacts: 'Contacts', nav: 'Navigation', screenReplacement: 'Screen Replacement', batteryReplacement: 'Battery Replacement', waterRepair: 'Water Damage Repair', cameraRepair: 'Camera Repair', orderRepair: 'Order Repair', trackRepair: 'Track Repair', login: 'Log In', register: 'Sign Up', copyright: '© 2026 MobiFIX PRO — Kharkiv. All rights reserved.', tagline: '12-month warranty · Free diagnosis · 4800+ repairs' },
    services_data: [
      { icon: '📱', name: 'Screen Replacement', desc: 'Cracks, broken display, unresponsive touchscreen. Original screens.', price: 'from ₴800', time: '30–60 min' },
      { icon: '🔋', name: 'Battery Replacement', desc: 'Short battery life, not charging, swollen battery', price: 'from ₴400', time: '20–40 min' },
      { icon: '📷', name: 'Camera Repair', desc: 'Blurry photos, not focusing, black camera screen', price: 'from ₴600', time: '40–90 min' },
      { icon: '💧', name: 'Water Damage', desc: "Dropped in water, won't turn on, flickering screen", price: 'from ₴500', time: '60–120 min' },
      { icon: '🔌', name: 'Charging Port', desc: 'Not charging, loose connection, USB not detected', price: 'from ₴350', time: '20–30 min' },
      { icon: '🔊', name: 'Speaker/Mic', desc: "Low sound, crackling, caller can't hear you", price: 'from ₴350', time: '30–50 min' },
      { icon: '🔧', name: 'Motherboard Repair', desc: "Won't turn on, freezes, no SIM signal — microchip level repair", price: 'from ₴1200', time: '1–3 days' },
      { icon: '💾', name: 'Data Recovery', desc: 'Recover photos, contacts, messages after reset or hardware failure', price: 'from ₴500', time: '1–4 hrs' },
      { icon: '🪟', name: 'Glass Only Replacement', desc: 'Only the outer glass is cracked — replaced without touching LCD', price: 'from ₴400', time: '60–90 min' },
      { icon: '📲', name: 'Software / Flashing', desc: 'Stuck on logo, crash loop, virus — full firmware restore', price: 'from ₴250', time: '30–60 min' },
      { icon: '🆔', name: 'Face ID / Touch ID Repair', desc: 'Fingerprint or face unlock not working — sensor replaced', price: 'from ₴900', time: '60–90 min' },
      { icon: '📶', name: 'Wi-Fi / Bluetooth Repair', desc: "Can't connect to network, weak signal — antenna repair", price: 'from ₴700', time: '60–120 min' },
    ],
    trackSteps: ['Accepted', 'Diagnosis', 'Repair', 'Testing', 'Ready'],
    reviews_data: [
      { initials: 'DK', author: 'Dmitry K.', device: 'iPhone 14 Pro · Screen', text: 'Fixed my iPhone in 40 minutes right in front of me. Excellent quality, gave a one-year warranty. Very satisfied!', date: '2 days ago' },
      { initials: 'AM', author: 'Anna M.', device: 'Samsung S23 · Battery', text: 'Battery was draining in 3 hours, replaced quickly and at a fair price. The repair tracking feature is very handy!', date: '5 days ago' },
      { initials: 'SP', author: 'Sergey P.', device: 'Xiaomi · Water Damage', text: 'Phone fell in water. Thought it was gone — but the techs saved it in 2 hours. Works like new!', date: '1 week ago' },
      { initials: 'KV', author: 'Kate V.', device: 'iPhone 13 · Camera', text: 'Main camera stopped working. Fixed in 50 minutes, fair price. Highly recommend!', date: '2 weeks ago' },
    ]
  }
};

export default {
  name: 'HomeNew',
  setup() {
    const authStore = useAuthStore();
    const locale = useLocaleStore();
    const isAuthenticated = computed(() => !!authStore.token);

    const T = computed(() => TX[locale.lang]);
    const services = computed(() => T.value.services_data);
    const howSteps = computed(() => T.value.how.steps);
    const trackSteps = computed(() => T.value.trackSteps);
    const reviews = computed(() => T.value.reviews_data);

    const calcBrand = ref('');
    const calcType = ref('');
    const trackNumber = ref('');
    const trackResult = ref(false);
    const chatInput = ref('');
    const chatMessages = ref([]);
    const chatMessagesEl = ref(null);

    // Initialize chat greeting from current language
    chatMessages.value = [{ role: 'bot', text: TX[locale.lang].chat.greeting, time: TX[locale.lang].hero.now }];

    // Re-init greeting on language change
    const initGreeting = () => {
      chatMessages.value = [{ role: 'bot', text: T.value.chat.greeting, time: T.value.hero.now }];
    };
    // Watch locale changes to update greeting
    let prevLang = locale.lang;
    const checkLang = setInterval(() => {
      if (locale.lang !== prevLang) { prevLang = locale.lang; initGreeting(); }
    }, 200);

    const prices = {
      // Аналіз цін: середні по сервісах Харкова (2026)
      apple:   { screen: ['800–15000 грн', '45–90 хв'], battery: ['900–1400 грн', '30 хв'], camera: ['1500–4500 грн', '60 хв'], charge: ['700–3000 грн', '30 хв'], water: ['1200–2500 грн', '2–4 год'], speaker: ['600–1200 грн', '35 хв'], board: ['2000–6000 грн', '1–3 дні'], glass: ['1100–4000 грн', '60–90 хв'], software: ['500–3000 грн', '30–60 хв'], faceid: ['1200–2500 грн', '60–90 хв'] },
      samsung: { screen: ['800–6000 грн', '40 хв'],  battery: ['500–900 грн', '25 хв'],  camera: ['900–4500 грн', '50 хв'],  charge: ['350–3000 грн', '25 хв'], water: ['800–1800 грн', '2–3 год'],  speaker: ['400–800 грн', '30 хв'],  board: ['2000–5000 грн', '1–3 дні'], glass: ['1100–3000 грн', '60–90 хв'], software: ['500–2000 грн', '30–60 хв'], faceid: ['700–1500 грн', '60 хв'] },
      xiaomi:  { screen: ['800–3000 грн', '35 хв'],  battery: ['350–600 грн', '20 хв'],  camera: ['600–3000 грн', '45 хв'],  charge: ['350–1500 грн', '20 хв'], water: ['600–1200 грн', '2–3 год'],  speaker: ['300–600 грн', '25 хв'],  board: ['2000–3500 грн', '1–3 дні'], glass: ['1100–2000 грн', '60 хв'],   software: ['500–1500 грн', '30 хв'],  faceid: ['500–900 грн', '60 хв'] },
      other:   { screen: ['800–3000 грн', '40 хв'],  battery: ['300–600 грн', '20 хв'],  camera: ['500–3000 грн', '50 хв'],  charge: ['350–1500 грн', '20 хв'], water: ['500–1200 грн', '2–3 год'],  speaker: ['280–600 грн', '25 хв'],  board: ['2000–4000 грн', '1–3 дні'], glass: ['1100–2000 грн', '60 хв'],   software: ['500–1500 грн', '30 хв'],  faceid: ['400–900 грн', '60 хв'] },
    };

    const calcPrice = computed(() => {
      if (calcBrand.value && calcType.value && prices[calcBrand.value]?.[calcType.value])
        return prices[calcBrand.value][calcType.value][0];
      return '';
    });
    const calcTime = computed(() => {
      if (calcBrand.value && calcType.value && prices[calcBrand.value]?.[calcType.value])
        return prices[calcBrand.value][calcType.value][1];
      return '';
    });

    // --- РЕАЛЬНИЙ ЧАТ З МАЙСТРОМ ---
    const chatSessionId = generateSessionId();
    const chatSending = ref(false);
    const { connect: socketConnect, joinChatRoom, onChatReply, offChatReply } = useSocket();

    function scrollChat() {
      nextTick(() => { if (chatMessagesEl.value) chatMessagesEl.value.scrollTop = chatMessagesEl.value.scrollHeight; });
    }

    function nowTime() {
      return new Date().toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    }

    async function sendMsg() {
      const text = chatInput.value.trim();
      if (!text || chatSending.value) return;
      chatMessages.value.push({ role: 'user', text, time: nowTime() });
      chatInput.value = '';
      chatSending.value = true;
      scrollChat();
      try {
        await axios.post(`${CHAT_API}/message`, {
          sessionId: chatSessionId,
          message: text,
          guestName: locale.lang === 'uk' ? 'Гість' : 'Guest'
        });
      } catch {
        // fallback — показуємо автовідповідь якщо немає бекенду
        setTimeout(() => {
          chatMessages.value.push({ role: 'bot', text: T.value.chat.replies[0], time: nowTime() });
          scrollChat();
        }, 800);
      } finally {
        chatSending.value = false;
      }
    }

    // Підключаємо socket і слухаємо відповіді майстра
    onMounted(() => {
      socketConnect();
      joinChatRoom(chatSessionId);
      onChatReply((data) => {
        chatMessages.value.push({ role: 'bot', text: data.text, time: data.time || nowTime() });
        scrollChat();
      });
    });

    onUnmounted(() => {
      offChatReply();
    });

    function trackRepair() { if (trackNumber.value.trim()) trackResult.value = true; }
    function scrollTo(id) { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }

    // --- ОНЛАЙН-ЗАПИС ---
    const bookingForm = ref({ name: '', phone: '', type: '', date: '', time: '', promo: '', delivery: 'personal', city: '', npBranch: '' });
    const bookingSent = ref(false);
    const bookingSending = ref(false);
    const promoResult = ref(null);
    const todayDate = new Date().toISOString().split('T')[0];

    const PROMO_CODES = {
      'FIRST10': { discount: '10%', label: '10% знижка на перший ремонт' },
      'MOBIFIX': { discount: 'Безкоштовна чистка', label: 'Безкоштовна чистка при ремонті' },
    };

    async function submitBooking() {
      if (!bookingForm.value.phone.trim()) return;
      if (bookingForm.value.promo) {
        const code = bookingForm.value.promo.toUpperCase();
        if (PROMO_CODES[code]) {
          promoResult.value = { valid: true, discount: PROMO_CODES[code].discount };
        } else {
          promoResult.value = { valid: false };
          return;
        }
      }
      bookingSending.value = true;
      const isMail = bookingForm.value.delivery === 'mail';
      const issueText = isMail
        ? `📦 ДОСТАВКА ПОШТОЮ | ${bookingForm.value.type}`
        : `🏪 ОСОБИСТИЙ ВІЗИТ | ${bookingForm.value.type} | ${bookingForm.value.date} ${bookingForm.value.time}`;
      const commentText = [
        isMail ? `Місто: ${bookingForm.value.city} | НП відділення: ${bookingForm.value.npBranch}` : '',
        bookingForm.value.promo ? `Промокод: ${bookingForm.value.promo}` : ''
      ].filter(Boolean).join(' | ') || 'Онлайн-запис';
      try {
        await axios.post((import.meta.env.VITE_API_URL || 'http://localhost:5000/api') + '/chat/callback', {
          name: bookingForm.value.name,
          phone: bookingForm.value.phone,
          issue: issueText,
          comment: commentText
        });
      } catch {}
      bookingSending.value = false;
      bookingSent.value = true;
    }

    return {
      locale, T, isAuthenticated,
      calcBrand, calcType, calcPrice, calcTime,
      trackNumber, trackResult, trackSteps, trackRepair,
      chatInput, chatMessages, chatMessagesEl, sendMsg, chatSending,
      services, howSteps, reviews, scrollTo,
      bookingForm, bookingSent, bookingSending, promoResult, todayDate, submitBooking,
    };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }
.pr-root { background: #f4f3ff; color: #1a1a2e; font-family: 'Segoe UI', system-ui, sans-serif; min-height: 100vh; }

/* NAV */
.pr-nav { position: sticky; top: 0; z-index: 100; background: rgba(255,255,255,0.95); backdrop-filter: blur(12px); border-bottom: 0.5px solid #e0dff8; box-shadow: 0 2px 20px rgba(83,74,183,0.07); }
.pr-nav-inner { max-width: 1200px; margin: 0 auto; padding: 0.9rem 2rem; display: flex; align-items: center; gap: 2rem; }
.pr-logo { font-size: 18px; font-weight: 700; color: #534ab7; letter-spacing: 0.5px; text-decoration: none; flex-shrink: 0; }
.pr-logo span { color: #1d9e75; }
.pr-nav-links { display: flex; gap: 1.75rem; flex: 1; }
.pr-nav-links a { color: #666; font-size: 14px; text-decoration: none; cursor: pointer; font-weight: 500; transition: color 0.15s; }
.pr-nav-links a:hover { color: #534ab7; }
.pr-nav-auth { display: flex; gap: 0.75rem; align-items: center; }
.pr-nav-link-plain { color: #666; font-size: 14px; text-decoration: none; font-weight: 500; transition: color 0.15s; }
.pr-nav-link-plain:hover { color: #534ab7; }
.pr-btn-primary-sm { background: #534ab7; color: #fff; border: none; padding: 8px 20px; border-radius: 8px; font-size: 14px; cursor: pointer; font-weight: 600; text-decoration: none; display: inline-block; transition: background 0.15s; }
.pr-btn-primary-sm:hover { background: #7f77dd; }
.pr-btn-outline-sm { background: transparent; color: #534ab7; border: 1.5px solid #534ab7; padding: 7px 16px; border-radius: 8px; font-size: 14px; cursor: pointer; font-weight: 500; text-decoration: none; display: inline-block; transition: all 0.15s; }
.pr-btn-outline-sm:hover { background: #eeedfe; }
.pr-lang-toggle { background: #f4f3ff; color: #534ab7; border: 1.5px solid #e0dff8; padding: 6px 12px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.pr-lang-toggle:hover { background: #eeedfe; border-color: #534ab7; }

/* HERO */
.pr-hero { position: relative; overflow: hidden; background: linear-gradient(135deg, #0f0c29 0%, #26215c 50%, #1a3a2a 100%); min-height: 580px; display: flex; align-items: center; }
.pr-hero-bg { position: absolute; inset: 0; overflow: hidden; }
.pr-blob { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.25; }
.pr-blob-1 { width: 500px; height: 500px; background: #534ab7; top: -200px; left: -100px; animation: blobFloat 8s ease-in-out infinite; }
.pr-blob-2 { width: 400px; height: 400px; background: #1d9e75; bottom: -150px; right: 10%; animation: blobFloat 10s ease-in-out infinite reverse; }
.pr-blob-3 { width: 300px; height: 300px; background: #7f77dd; top: 50%; right: 25%; animation: blobFloat 6s ease-in-out infinite 2s; }
@keyframes blobFloat { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-30px) scale(1.05); } }
.pr-grid-dots { position: absolute; inset: 0; background-image: radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px); background-size: 32px 32px; }
.pr-hero-inner { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 5rem 2rem 4rem; display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; align-items: center; }
.pr-hero-content { color: #fff; }
.pr-hero-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(29,158,117,0.2); border: 1px solid rgba(29,158,117,0.4); color: #4de0a8; font-size: 11px; font-weight: 700; letter-spacing: 1px; padding: 5px 14px; border-radius: 20px; margin-bottom: 1.5rem; }
.pr-badge-dot { width: 6px; height: 6px; background: #4de0a8; border-radius: 50%; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }
.pr-hero-title { font-size: 48px; font-weight: 800; line-height: 1.1; margin-bottom: 1.25rem; }
.pr-hero-gradient { background: linear-gradient(135deg, #c7c4f5, #4de0a8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.pr-hero-desc { color: rgba(255,255,255,0.7); font-size: 16px; line-height: 1.75; margin-bottom: 2rem; white-space: pre-line; }
.pr-hero-btns { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 2rem; }
.pr-btn-hero-primary { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border: none; padding: 13px 28px; border-radius: 10px; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; text-decoration: none; box-shadow: 0 4px 20px rgba(83,74,183,0.4); transition: transform 0.2s, box-shadow 0.2s; }
.pr-btn-hero-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(83,74,183,0.55); }
.pr-btn-hero-outline { background: transparent; color: #fff; border: 1.5px solid rgba(255,255,255,0.3); padding: 12px 26px; border-radius: 10px; font-size: 15px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
.pr-btn-hero-outline:hover { border-color: rgba(255,255,255,0.7); background: rgba(255,255,255,0.07); }
.pr-hero-trust { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.pr-trust-item { font-size: 13px; color: rgba(255,255,255,0.55); }
.pr-trust-sep { color: rgba(255,255,255,0.25); }

/* PHONE MOCKUP */
.pr-hero-visual { position: relative; display: flex; justify-content: center; align-items: center; }
.pr-phone-mockup { position: relative; }
.pr-phone-shell { width: 230px; height: 460px; background: linear-gradient(160deg, #1e1b3a, #2d2850); border-radius: 36px; border: 2px solid rgba(255,255,255,0.12); padding: 12px; box-shadow: 0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08); animation: phoneFloat 4s ease-in-out infinite; }
@keyframes phoneFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
.pr-phone-screen { width: 100%; height: 100%; background: linear-gradient(170deg, #1a1730, #0d1117); border-radius: 26px; overflow: hidden; padding: 16px 12px; }
.pr-phone-header { display: flex; justify-content: center; gap: 8px; margin-bottom: 14px; align-items: center; }
.pr-phone-cam { width: 10px; height: 10px; background: #2d2a4a; border-radius: 50%; }
.pr-phone-speaker { width: 38px; height: 5px; background: #2d2a4a; border-radius: 3px; }
.pr-phone-status { background: rgba(83,74,183,0.15); border: 1px solid rgba(83,74,183,0.3); border-radius: 14px; padding: 10px; margin-bottom: 8px; }
.pr-status-bar { font-size: 8px; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.pr-status-num { font-size: 12px; font-weight: 700; color: #c7c4f5; margin-bottom: 10px; }
.pr-phone-steps { display: flex; justify-content: space-between; margin-bottom: 8px; }
.pr-phone-step { display: flex; flex-direction: column; align-items: center; gap: 3px; flex: 1; }
.ps-circle { width: 20px; height: 20px; border-radius: 50%; background: #534ab7; display: flex; align-items: center; justify-content: center; font-size: 8px; color: #fff; font-weight: 700; }
.ps-active { background: #1d9e75; animation: pulse 2s infinite; }
.ps-grey { background: #2d2a4a; color: #555; }
.ps-text { font-size: 7px; color: #666; text-align: center; }
.ps-active-text { color: #4de0a8; font-weight: 700; }
.pr-phone-progress-wrap { height: 3px; background: #2d2a4a; border-radius: 2px; margin-bottom: 5px; overflow: hidden; }
.pr-phone-progress-bar { height: 100%; width: 55%; background: linear-gradient(90deg, #534ab7, #1d9e75); border-radius: 2px; }
.pr-phone-eta { font-size: 8px; color: #4de0a8; text-align: center; }
.pr-phone-chat-preview { background: rgba(255,255,255,0.03); border-radius: 8px; padding: 7px 9px; display: flex; justify-content: space-between; align-items: center; margin-top: 6px; }
.pr-pcp-msg { font-size: 8px; color: #777; }
.pr-pcp-time { font-size: 7px; color: #444; }
.pr-phone-glow { position: absolute; width: 180px; height: 280px; border-radius: 50%; filter: blur(60px); opacity: 0.18; pointer-events: none; }
.pr-glow-left { background: #534ab7; left: -70px; top: 50%; transform: translateY(-50%); }
.pr-glow-right { background: #1d9e75; right: -70px; top: 50%; transform: translateY(-50%); }
.pr-float-card { position: absolute; background: rgba(255,255,255,0.97); border-radius: 14px; padding: 9px 13px; display: flex; align-items: center; gap: 9px; box-shadow: 0 8px 32px rgba(0,0,0,0.2); }
.pr-float-1 { top: 20px; left: -80px; animation: floatCard1 4s ease-in-out infinite; }
.pr-float-2 { bottom: 50px; left: -70px; animation: floatCard2 5s ease-in-out infinite 1s; }
.pr-float-3 { top: 40%; right: -60px; animation: floatCard1 6s ease-in-out infinite 0.5s; }
@keyframes floatCard1 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes floatCard2 { 0%,100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
.pr-fc-icon { font-size: 20px; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; background: #eeedfe; border-radius: 9px; }
.pr-fc-green { background: #e1f5ee; }
.pr-fc-gold { background: #fef9e7; }
.pr-fc-title { font-size: 12px; font-weight: 700; color: #26215c; }
.pr-fc-sub { font-size: 11px; color: #666; }

/* STATS */
.pr-stats { display: flex; justify-content: center; align-items: center; background: #fff; border-bottom: 0.5px solid #e0dff8; flex-wrap: wrap; }
.pr-stat { padding: 1.75rem 3rem; text-align: center; }
.pr-stat-num { font-size: 28px; font-weight: 800; color: #534ab7; }
.pr-stat-label { font-size: 12px; color: #666; margin-top: 4px; }
.pr-stat-sep { width: 1px; height: 48px; background: #e0dff8; }

/* SECTIONS */
.pr-section { max-width: 1200px; margin: 0 auto; padding: 4.5rem 2rem; }
.pr-section-header { text-align: center; margin-bottom: 3rem; }
.pr-section-tag { display: inline-block; background: #eeedfe; color: #534ab7; font-size: 11px; font-weight: 800; letter-spacing: 1.5px; padding: 4px 12px; border-radius: 20px; margin-bottom: 1rem; }
.pr-section-title { font-size: 34px; font-weight: 800; color: #26215c; margin-bottom: 0.75rem; }
.pr-section-sub { font-size: 15px; color: #666; line-height: 1.6; }

/* CALCULATOR */
.pr-calc-box { background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 2rem; }
.pr-calc-selects { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.5rem; }
.pr-calc-sel-wrap { display: flex; flex-direction: column; gap: 6px; }
.pr-calc-label-sm { font-size: 12px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
.pr-calc-select { background: #f4f3ff; border: 1.5px solid #e0dff8; border-radius: 10px; padding: 11px 14px; font-size: 15px; color: #26215c; outline: none; transition: border-color 0.15s; }
.pr-calc-select:focus { border-color: #534ab7; background: #fff; }
.pr-calc-result-wrap { border: 2px dashed #e0dff8; border-radius: 14px; padding: 1.5rem; transition: border-color 0.3s; }
.pr-calc-result-wrap.active { border-color: #534ab7; border-style: solid; }
.pr-calc-placeholder { display: flex; align-items: center; gap: 1rem; color: #bbb; font-size: 14px; }
.pr-calc-ph-icon { font-size: 32px; }
.pr-calc-result { display: flex; justify-content: space-between; align-items: center; }
.pr-calc-result-label { font-size: 12px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.pr-calc-result-price { font-size: 36px; font-weight: 800; color: #1d9e75; }
.pr-calc-result-time { font-size: 13px; color: #666; margin-top: 4px; }
.pr-btn-calc { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; padding: 14px 28px; border-radius: 12px; font-size: 16px; font-weight: 700; text-decoration: none; display: inline-block; box-shadow: 0 4px 16px rgba(83,74,183,0.3); transition: transform 0.2s; }
.pr-btn-calc:hover { transform: translateY(-2px); }

/* SERVICES GRID */
.pr-services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; }
.pr-service-card { background: #fff; border: 1px solid #e0dff8; border-radius: 18px; padding: 1.75rem; display: flex; flex-direction: column; gap: 0.5rem; transition: box-shadow 0.2s, transform 0.2s; }
.pr-service-card:hover { box-shadow: 0 8px 32px rgba(83,74,183,0.1); transform: translateY(-2px); }
.pr-sc-icon { font-size: 36px; margin-bottom: 0.5rem; }
.pr-sc-name { font-size: 16px; font-weight: 800; color: #26215c; }
.pr-sc-desc { font-size: 13px; color: #666; line-height: 1.5; flex: 1; }
.pr-sc-footer { display: flex; justify-content: space-between; padding: 10px 0; border-top: 1px solid #f0effc; margin-top: auto; }
.pr-sc-price { font-size: 15px; font-weight: 800; color: #1d9e75; }
.pr-sc-time { font-size: 12px; color: #aaa; }
.pr-sc-btn { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border: none; width: 100%; padding: 10px; border-radius: 9px; font-size: 14px; font-weight: 700; cursor: pointer; text-decoration: none; display: block; text-align: center; transition: opacity 0.15s; margin-top: 0.75rem; }
.pr-sc-btn:hover { opacity: 0.85; }

/* HOW WRAP */
.pr-how-wrap { background: #fff; }
.pr-how-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.5rem; }
.pr-how-step { text-align: center; padding: 1.5rem 1rem; position: relative; }
.pr-how-num { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; font-size: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; }
.pr-how-icon { font-size: 32px; margin-bottom: 0.75rem; }
.pr-how-title { font-size: 16px; font-weight: 800; color: #26215c; margin-bottom: 0.5rem; }
.pr-how-desc { font-size: 13px; color: #666; line-height: 1.6; }

/* TRACKING */
.pr-track-box { background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 2rem; }
.pr-track-input-row { display: flex; gap: 12px; margin-bottom: 1.5rem; }
.pr-track-input { flex: 1; background: #f4f3ff; border: 1.5px solid #e0dff8; border-radius: 10px; padding: 12px 16px; font-size: 15px; color: #26215c; outline: none; transition: border-color 0.15s; }
.pr-track-input:focus { border-color: #534ab7; background: #fff; }
.pr-track-btn { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border: none; padding: 12px 28px; border-radius: 10px; font-size: 15px; font-weight: 700; cursor: pointer; transition: opacity 0.15s; }
.pr-track-btn:hover { opacity: 0.85; }
.pr-track-result { background: #f4f3ff; border-radius: 14px; padding: 1.5rem; }
.pr-track-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; }
.pr-track-order-num { font-size: 18px; font-weight: 800; color: #26215c; }
.pr-track-device { font-size: 13px; color: #666; margin-top: 4px; }
.pr-track-status-badge { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; font-size: 12px; font-weight: 700; padding: 6px 14px; border-radius: 20px; }
.pr-steps-row { display: flex; align-items: center; margin-bottom: 1.25rem; }
.pr-step-item { display: flex; align-items: center; flex: 1; position: relative; }
.pr-step-item:last-child { flex: 0; }
.pr-step-circle { width: 36px; height: 36px; border-radius: 50%; background: #e0dff8; display: flex; align-items: center; justify-content: center; font-size: 14px; color: #666; font-weight: 700; z-index: 1; position: relative; flex-shrink: 0; }
.pr-step-item.done .pr-step-circle { background: #534ab7; color: #fff; }
.pr-step-item.active .pr-step-circle { background: #1d9e75; color: #fff; animation: pulse 2s infinite; }
.pr-step-label { font-size: 11px; color: #666; position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%); white-space: nowrap; }
.pr-step-item.done .pr-step-label, .pr-step-item.active .pr-step-label { color: #26215c; font-weight: 700; }
.pr-step-line { flex: 1; height: 2px; background: #e0dff8; margin: 0 4px; }
.pr-step-line.active { background: #534ab7; }
.pr-track-note { background: #f4f3ff; border-radius: 10px; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; font-size: 13px; color: #666; margin-top: 2rem; }
.pr-track-notify { color: #1d9e75; font-weight: 700; font-size: 12px; }
.fade-enter-active, .fade-leave-active { transition: all 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(10px); }

/* CHAT */
.pr-chat-box { background: #fff; border: 1px solid #e0dff8; border-radius: 20px; overflow: hidden; box-shadow: 0 4px 24px rgba(83,74,183,0.07); }
.pr-chat-header { padding: 1.25rem 1.5rem; border-bottom: 0.5px solid #e0dff8; display: flex; align-items: center; gap: 12px; background: linear-gradient(135deg, #eeedfe, #f4f3ff); }
.pr-chat-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #534ab7, #7f77dd); display: flex; align-items: center; justify-content: center; font-size: 14px; color: #fff; font-weight: 700; flex-shrink: 0; }
.pr-chat-name { font-size: 15px; font-weight: 700; color: #26215c; }
.pr-chat-status { font-size: 12px; color: #1d9e75; font-weight: 500; display: flex; align-items: center; gap: 5px; }
.pr-online-dot { width: 7px; height: 7px; background: #1d9e75; border-radius: 50%; animation: pulse 2s infinite; display: inline-block; }
.pr-chat-badges { margin-left: auto; display: flex; gap: 6px; }
.pr-chat-badge { background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 4px 10px; font-size: 11px; color: #666; }
.pr-chat-messages { padding: 1.25rem; min-height: 200px; display: flex; flex-direction: column; gap: 12px; max-height: 280px; overflow-y: auto; background: #fafafe; }
.pr-msg { max-width: 80%; }
.pr-msg-text { font-size: 14px; line-height: 1.55; padding: 10px 14px; border-radius: 12px; }
.pr-msg-time { font-size: 10px; color: #bbb; margin-top: 4px; }
.pr-msg.bot .pr-msg-text { background: #eeedfe; color: #3c3489; border-radius: 4px 12px 12px 12px; }
.pr-msg.user { align-self: flex-end; }
.pr-msg.user .pr-msg-text { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border-radius: 12px 4px 12px 12px; }
.pr-msg.user .pr-msg-time { text-align: right; }
.pr-chat-input-row { padding: 0.75rem 1rem; border-top: 0.5px solid #e0dff8; display: flex; gap: 8px; }
.pr-chat-input { flex: 1; background: #f4f3ff; border: 1.5px solid #e0dff8; border-radius: 10px; padding: 10px 14px; color: #26215c; font-size: 14px; outline: none; transition: border-color 0.15s; }
.pr-chat-input:focus { border-color: #534ab7; }
.pr-chat-send { background: linear-gradient(135deg, #534ab7, #7f77dd); border: none; color: #fff; padding: 10px 18px; border-radius: 10px; cursor: pointer; font-size: 16px; font-weight: 700; transition: opacity 0.15s; }
.pr-chat-send:hover { opacity: 0.85; }

/* REVIEWS */
.pr-reviews-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.25rem; }
.pr-review-card { background: #fff; border: 1px solid #e0dff8; border-radius: 16px; padding: 1.5rem; }
.pr-review-top { display: flex; align-items: center; gap: 10px; margin-bottom: 1rem; }
.pr-review-avatar { width: 38px; height: 38px; border-radius: 50%; background: linear-gradient(135deg, #534ab7, #1d9e75); display: flex; align-items: center; justify-content: center; font-size: 13px; color: #fff; font-weight: 700; flex-shrink: 0; }
.pr-review-author { font-size: 14px; font-weight: 700; color: #26215c; }
.pr-review-device { font-size: 12px; color: #666; }
.pr-review-stars { color: #ef9f27; font-size: 13px; margin-left: auto; }
.pr-review-text { font-size: 13px; color: #5f5e5a; line-height: 1.65; margin-bottom: 10px; }
.pr-review-date { font-size: 11px; color: #bbb; }

/* BOOKING */
.pr-booking-wrap { display: grid; grid-template-columns: 1fr 360px; gap: 2rem; align-items: start; }
.pr-booking-form { background: #fff; border-radius: 20px; padding: 2rem; box-shadow: 0 4px 24px rgba(83,74,183,0.08); border: 1px solid #e0dff8; }
.pr-booking-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.25rem; }
.pr-booking-field { display: flex; flex-direction: column; gap: 6px; }
.pr-booking-label { font-size: 12px; font-weight: 700; color: #555; }
.pr-booking-input { background: #f4f3ff; border: 1.5px solid #e0dff8; border-radius: 10px; padding: 10px 14px; font-size: 14px; color: #26215c; outline: none; width: 100%; font-family: inherit; transition: border-color 0.15s; }
.pr-booking-input:focus { border-color: #534ab7; background: #fff; }
.pr-booking-promo-hint { margin-bottom: 1rem; font-size: 13px; }
.pr-promo-ok { color: #1d9e75; font-weight: 600; }
.pr-promo-bad { color: #e05252; font-weight: 600; }
.pr-booking-btn { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border: none; padding: 14px; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; width: 100%; transition: opacity 0.15s; }
.pr-booking-btn:hover:not(:disabled) { opacity: 0.9; }
.pr-booking-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.pr-booking-success { background: #fff; border-radius: 20px; padding: 3rem 2rem; text-align: center; box-shadow: 0 4px 24px rgba(83,74,183,0.08); border: 1px solid #e0dff8; }
.pr-booking-success-icon { font-size: 56px; margin-bottom: 1rem; }
.pr-booking-success h3 { font-size: 22px; font-weight: 800; color: #1d9e75; margin-bottom: 0.75rem; }
.pr-booking-success p { font-size: 15px; color: #555; line-height: 1.7; margin-bottom: 1.5rem; }
.pr-booking-aside { display: flex; flex-direction: column; gap: 1rem; }
.pr-booking-info-card { background: #fff; border-radius: 14px; padding: 1.25rem; border: 1px solid #e0dff8; display: flex; gap: 12px; align-items: flex-start; }
.pr-booking-info-icon { font-size: 22px; flex-shrink: 0; }
.pr-booking-info-title { font-size: 14px; font-weight: 700; color: #26215c; margin-bottom: 3px; }
.pr-booking-info-desc { font-size: 12px; color: #666; line-height: 1.5; }
.pr-booking-promo-block { background: linear-gradient(135deg, #f4f3ff, #eaf7f2); border-radius: 14px; padding: 1.25rem; border: 1px solid #e0dff8; }
.pr-mail-aside-card { background: linear-gradient(135deg, #eaf7f2, #f4f3ff); border-color: #b2e8d4; }
/* Delivery toggle */
.pr-delivery-toggle { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 1.25rem; }
.pr-dtoggle-btn { padding: 10px; border: 2px solid #e0dff8; border-radius: 10px; background: #f4f3ff; color: #666; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
.pr-dtoggle-btn.active { border-color: #534ab7; background: #534ab7; color: #fff; }
.pr-dtoggle-btn:hover:not(.active) { border-color: #534ab7; color: #534ab7; }
/* Mail steps */
.pr-mail-info { background: linear-gradient(135deg, #eaf7f2, #f0efff); border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 1rem; border: 1px solid #b2e8d4; display: flex; flex-direction: column; gap: 8px; }
.pr-mail-step { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #333; }
.pr-mail-num { width: 24px; height: 24px; background: #1d9e75; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.pr-booking-promo-title { font-size: 13px; font-weight: 700; color: #26215c; margin-bottom: 10px; }
.pr-promo-item { font-size: 13px; color: #555; margin-bottom: 6px; }
.pr-promo-item code { background: #534ab7; color: #fff; padding: 2px 8px; border-radius: 6px; font-size: 12px; font-weight: 700; margin-right: 6px; }

/* LOYALTY */
.pr-loyalty-section { background: linear-gradient(135deg, #26215c 0%, #1a3a2a 100%); padding: 4rem 2rem; }
.pr-loyalty-inner { max-width: 1200px; margin: 0 auto; }
.pr-loyalty-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; margin-bottom: 2.5rem; }
.pr-loyalty-card { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 20px; padding: 2rem; text-align: center; backdrop-filter: blur(8px); transition: transform 0.2s; }
.pr-loyalty-card:hover { transform: translateY(-4px); background: rgba(255,255,255,0.12); }
.pr-loyalty-icon { font-size: 44px; margin-bottom: 1rem; }
.pr-loyalty-title { font-size: 18px; font-weight: 800; color: #fff; margin-bottom: 0.75rem; }
.pr-loyalty-desc { font-size: 14px; color: rgba(255,255,255,0.7); line-height: 1.6; }
.pr-loyalty-desc strong { color: #4ade80; }
.pr-loyalty-cta { text-align: center; }
.pr-loyalty-btn { display: inline-block; background: #1d9e75; color: #fff; padding: 14px 32px; border-radius: 12px; font-size: 16px; font-weight: 700; text-decoration: none; transition: background 0.2s; margin-bottom: 1rem; }
.pr-loyalty-btn:hover { background: #25bf8e; }
.pr-loyalty-note { font-size: 14px; color: rgba(255,255,255,0.6); }
.pr-loyalty-link { color: #4ade80; text-decoration: none; font-weight: 600; }
.pr-loyalty-link:hover { text-decoration: underline; }

/* CONTACTS / MAP */
.pr-contacts-wrap { display: grid; grid-template-columns: 300px 1fr; gap: 1.5rem; align-items: start; }
.pr-contacts-info { display: flex; flex-direction: column; gap: 1rem; }
.pr-contact-card { background: #fff; border: 1px solid #e0dff8; border-radius: 14px; padding: 1rem 1.25rem; display: flex; align-items: center; gap: 1rem; text-decoration: none; transition: box-shadow 0.15s; }
.pr-contact-card-link:hover { box-shadow: 0 4px 16px rgba(83,74,183,0.1); }
.pr-contact-icon { font-size: 22px; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; background: #f4f3ff; border-radius: 12px; flex-shrink: 0; }
.pr-contact-label { font-size: 11px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
.pr-contact-val { font-size: 14px; font-weight: 600; color: #26215c; margin-top: 2px; }
.pr-contact-purple { color: #534ab7; }
.pr-contact-cta-btn { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; padding: 13px; border-radius: 12px; font-size: 15px; font-weight: 700; text-decoration: none; text-align: center; box-shadow: 0 4px 16px rgba(83,74,183,0.3); transition: transform 0.15s; display: block; }
.pr-contact-cta-btn:hover { transform: translateY(-2px); }
.pr-map-wrap { border-radius: 20px; overflow: hidden; border: 1px solid #e0dff8; height: 380px; box-shadow: 0 4px 24px rgba(83,74,183,0.07); }
.pr-map-iframe { width: 100%; height: 100%; border: 0; display: block; }

/* CTA */
.pr-cta-section { background: linear-gradient(135deg, #26215c, #1a3a2a); padding: 5rem 2rem; text-align: center; }
.pr-cta-inner { max-width: 600px; margin: 0 auto; color: #fff; }
.pr-cta-icon { font-size: 52px; margin-bottom: 1rem; }
.pr-cta-inner h2 { font-size: 36px; font-weight: 800; margin-bottom: 1rem; }
.pr-cta-inner p { font-size: 16px; color: rgba(255,255,255,0.65); line-height: 1.75; margin-bottom: 2rem; }
.pr-cta-btns { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
.pr-btn-hero-outline-light { background: transparent; color: #fff; border: 1.5px solid rgba(255,255,255,0.4); padding: 12px 26px; border-radius: 10px; font-size: 15px; font-weight: 500; cursor: pointer; text-decoration: none; display: inline-block; transition: all 0.2s; }
.pr-btn-hero-outline-light:hover { border-color: rgba(255,255,255,0.75); background: rgba(255,255,255,0.07); }

/* FOOTER */
.pr-footer { background: #1a1730; color: #ccc; }
.pr-footer-inner { max-width: 1200px; margin: 0 auto; padding: 3rem 2rem 2rem; display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr; gap: 2.5rem; }
.pr-footer-brand p { font-size: 13px; color: #aaa; line-height: 1.8; margin-top: 0.75rem; white-space: pre-line; }
.pr-footer-logo { font-size: 18px; font-weight: 700; color: #fff; }
.pr-footer-logo span { color: #1d9e75; }
.pr-footer-col { display: flex; flex-direction: column; gap: 10px; }
.pr-footer-title { font-size: 11px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
.pr-footer-link { font-size: 13px; color: #aaa; text-decoration: none; transition: color 0.15s; cursor: pointer; }
.pr-footer-link:hover { color: #c7c4f5; }
.pr-footer-bottom { max-width: 1200px; margin: 0 auto; padding: 1.25rem 2rem; border-top: 1px solid #2d2a4a; display: flex; justify-content: space-between; font-size: 12px; color: #999; }

@media (max-width: 900px) {
  .pr-hero-inner { grid-template-columns: 1fr; }
  .pr-hero-visual { display: none; }
  .pr-stats { flex-wrap: wrap; }
  .pr-stat-sep { display: none; }
  .pr-how-steps { grid-template-columns: 1fr 1fr; }
  .pr-footer-inner { grid-template-columns: 1fr 1fr; }
  .pr-footer-bottom { flex-direction: column; gap: 6px; }
  .pr-hero-title { font-size: 34px; }
  .pr-calc-selects { grid-template-columns: 1fr; }
  .pr-contacts-wrap { grid-template-columns: 1fr; }
  .pr-map-wrap { height: 280px; }
}
@media (max-width: 600px) {
  .pr-nav-links { display: none; }
  .pr-how-steps { grid-template-columns: 1fr; }
  .pr-hero-title { font-size: 28px; }
  .pr-section { padding: 2.5rem 1rem; }
  .pr-services-grid { grid-template-columns: 1fr; }
  .pr-about-grid { grid-template-columns: 1fr; }
  .pr-about-cards-col { grid-template-columns: 1fr; }
}

/* ===== ABOUT SECTION ===== */
.pr-about-section { background: #fff; border-radius: 28px; margin: 0 auto 2rem; text-align: center; }
.pr-about-section .pr-section-header { text-align: center; align-items: center; display: flex; flex-direction: column; }
.pr-about-grid { text-align: left; }
.pr-about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; margin-top: 1rem; }
.pr-about-text-col { display: flex; flex-direction: column; gap: 1rem; }
.pr-about-p { font-size: 15px; color: #444; line-height: 1.75; }
.pr-about-certs { display: flex; flex-direction: column; gap: 10px; margin-top: 1rem; }
.pr-about-cert { display: flex; align-items: center; gap: 12px; background: #f4f3ff; border-radius: 12px; padding: 12px 16px; }
.pr-about-cert-icon { font-size: 22px; flex-shrink: 0; }
.pr-about-cert-label { font-size: 13px; font-weight: 700; color: #26215c; }
.pr-about-cert-val { font-size: 12px; color: #666; margin-top: 2px; }
.pr-about-cards-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; align-content: start; }
.pr-about-card { background: #f4f3ff; border-radius: 16px; padding: 1.25rem; display: flex; flex-direction: column; gap: 8px; transition: box-shadow 0.2s; }
.pr-about-card:hover { box-shadow: 0 4px 20px rgba(83,74,183,0.12); }
.pr-about-card-icon { font-size: 28px; }
.pr-about-card-title { font-size: 14px; font-weight: 800; color: #26215c; }
.pr-about-card-desc { font-size: 13px; color: #666; line-height: 1.5; }
@media (max-width: 900px) {
  .pr-about-grid { grid-template-columns: 1fr; gap: 2rem; }
}
</style>
